import { useEffect, useState } from 'react'
import { api } from '../../lib/api'
import { StatusBadge } from '../ui/StatusBadge'
import { Badge } from '../ui/Badge'
import { CapacityBar } from './CapacityBar'
import { DeviceLifecycleTimeline } from './DeviceLifecycleTimeline'
import { DeviceCredentialsPanel } from './DeviceCredentialsPanel'
import { Button } from '../ui/Button'
import type { SiteDeviceWithSpec } from '@shared/types'
import type { DeviceInterface } from '@shared/types'

const CATEGORY_ICONS: Record<string, string> = {
  camera: '◉', nvr: '▣', vms_server: '▣', switch: '⬡', patch_panel: '⬡',
  controller: '◈', reader: '◈', speaker: '♫', ups: '⚡', pdu: '⚡',
  router: '⬡', access_point: '⬡', intercom: '♫', other: '●',
}
const CATEGORY_COLORS: Record<string, string> = {
  camera: 'var(--color-camera)', nvr: 'var(--color-networking)', vms_server: 'var(--color-networking)', switch: 'var(--color-networking)',
  patch_panel: 'var(--color-networking)', controller: 'var(--color-access)', reader: 'var(--color-access)',
  speaker: 'var(--color-software)', ups: 'var(--color-power)', pdu: 'var(--color-power)',
  router: 'var(--color-networking)', access_point: 'var(--color-networking)', intercom: 'var(--color-software)', other: 'var(--color-infra)',
}

interface DeviceWithInterfaces extends SiteDeviceWithSpec {
  interfaces: DeviceInterface[]
}

interface Props {
  deviceSlug: string
  onClose: () => void
}

export function DeviceDetailPanel({ deviceSlug, onClose }: Props) {
  const [device, setDevice] = useState<DeviceWithInterfaces | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    api.get<DeviceWithInterfaces>(`/site-devices/${deviceSlug}`)
      .then(setDevice)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [deviceSlug])

  if (loading) {
    return (
      <div style={{
        position: 'fixed', right: 0, top: 0, bottom: 0, width: 420,
        background: 'var(--bg-surface)', boxShadow: 'var(--shadow-lg)', zIndex: 30,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderLeft: '1px solid var(--border)',
      }}>
        <div className="skeleton" style={{ width: 200, height: 20 }} />
      </div>
    )
  }

  if (!device) return null

  const cat = device.spec_category || device.device_type || 'other'
  const icon = CATEGORY_ICONS[cat] || '●'
  const color = CATEGORY_COLORS[cat] || 'var(--color-infra)'
  const isAP = cat === 'access_point' || device.device_type === 'access_point'
  const isGateway = cat === 'gateway' || device.device_type === 'gateway' || cat === 'router'

  return (
    <div style={{
      position: 'fixed', right: 0, top: 0, bottom: 0, width: 420,
      background: 'var(--bg-surface)', boxShadow: 'var(--shadow-lg)', zIndex: 30,
      display: 'flex', flexDirection: 'column',
      borderLeft: '1px solid var(--border)',
      animation: 'slide-in 0.2s ease-out',
    }}>
      {/* Header */}
      <div className="flex items-center justify-between" style={{
        padding: '16px 20px', borderBottom: '1px solid var(--border-light)', flexShrink: 0,
      }}>
        <div className="flex items-center gap-3">
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: `${color}15`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 15, color,
          }}>{icon}</div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{device.label || `${device.spec_brand} ${device.spec_model}`}</div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{device.spec_brand} {device.spec_model}</div>
          </div>
        </div>
        <div
          onClick={onClose}
          style={{
            cursor: 'pointer', fontSize: 20, color: 'var(--text-tertiary)',
            width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 6, background: 'var(--bg-inset)',
          }}
        >&times;</div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {/* Status badges */}
        <div className="flex flex-wrap gap-2" style={{ marginBottom: 16 }}>
          <StatusBadge status={device.status} />
          {device.installed_date && <Badge>Installed {device.installed_date}</Badge>}
          {device.warranty_expires && <Badge>Warranty &rarr; {device.warranty_expires}</Badge>}
        </div>

        {/* Capacity */}
        {device.spec_max_camera_channels && (
          <Section title="Capacity">
            <CapacityBar
              used={device.channels_used ?? 0}
              max={device.spec_max_camera_channels}
              label="Camera Channels" unit="ch" color="var(--color-camera)"
            />
          </Section>
        )}

        {device.spec_total_ports && (
          <Section title={device.spec_max_camera_channels ? '' : 'Capacity'}>
            <CapacityBar
              used={device.ports_used ?? 0}
              max={device.spec_total_ports}
              label="Ports" unit="ports" color="var(--primary)"
            />
          </Section>
        )}

        {device.spec_poe_budget_watts && (
          <Section title="">
            <CapacityBar
              used={device.poe_watts_used ?? 0}
              max={device.spec_poe_budget_watts}
              label="PoE Budget" unit="W" color="var(--warning)" warnAt={0.7}
            />
          </Section>
        )}

        {/* Storage */}
        {device.actual_storage_tb && (
          <Section title="Storage">
            <div className="flex gap-2">
              <div style={{ flex: 1, padding: '10px 12px', background: 'var(--bg-inset)', borderRadius: 'var(--radius)' }}>
                <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>Total</div>
                <div style={{ fontSize: 15, fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{device.actual_storage_tb} TB</div>
              </div>
              {device.storage_used_tb != null && (
                <div style={{ flex: 1, padding: '10px 12px', background: 'var(--bg-inset)', borderRadius: 'var(--radius)' }}>
                  <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>Used</div>
                  <div style={{ fontSize: 15, fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{device.storage_used_tb} TB</div>
                </div>
              )}
            </div>
          </Section>
        )}

        {/* Network */}
        {device.ip_address && (
          <Section title="Network">
            <div style={{ background: 'var(--bg-inset)', borderRadius: 'var(--radius-lg)', padding: 14 }}>
              {([
                ['IP Address', device.ip_address],
                device.mac_address && ['MAC', device.mac_address],
                device.hostname && ['Hostname', device.hostname],
                device.serial_number && ['Serial', device.serial_number],
              ].filter(Boolean) as [string, string][]).map(([k, v], i) => (
                <div key={i} className="flex justify-between" style={{ padding: '5px 0' }}>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{k}</span>
                  <span style={{ fontSize: 12, fontWeight: 500, fontFamily: 'var(--font-mono)' }}>{v}</span>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* AP-specific: Wireless Clients */}
        {isAP && (
          <APWirelessClients siteId={device.site_id} apMac={device.mac_address} apName={device.label || device.hostname} />
        )}

        {/* Gateway-specific: WAN & Uplink */}
        {isGateway && (
          <Section title="Gateway Info">
            <div style={{ background: 'var(--bg-inset)', borderRadius: 'var(--radius-lg)', padding: 14 }}>
              {([
                ['Type', 'Security Gateway'],
                device.device_model && ['Model', device.device_model],
                device.actual_firmware && ['Firmware', device.actual_firmware],
                device.online_since && ['Online Since', new Date(device.online_since).toLocaleDateString()],
                device.port_count && ['Ports', String(device.port_count)],
              ].filter(Boolean) as [string, string][]).map(([k, v], i) => (
                <div key={i} className="flex justify-between" style={{ padding: '5px 0' }}>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{k}</span>
                  <span style={{ fontSize: 12, fontWeight: 500, fontFamily: 'var(--font-mono)' }}>{v}</span>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Rack position */}
        {device.rack_position_u && (
          <Section title="Physical">
            <div style={{ fontSize: 13 }}>
              Rack U{device.rack_position_u}
            </div>
            {device.serial_number && (
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4 }}>
                S/N: <span style={{ fontFamily: 'var(--font-mono)' }}>{device.serial_number}</span>
              </div>
            )}
          </Section>
        )}

        {/* Notes */}
        {device.notes && (
          <Section title="Notes">
            <div style={{
              fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6,
              padding: '10px 14px', background: 'var(--accent-soft)',
              borderRadius: 'var(--radius)', borderLeft: '3px solid var(--accent)',
            }}>{device.notes}</div>
          </Section>
        )}

        {/* Lifecycle Timeline */}
        <Section title="">
          <DeviceLifecycleTimeline
            siteId={device.site_id}
            deviceId={device.id}
            deviceLabel={device.label || `${device.spec_brand} ${device.spec_model}`}
          />
        </Section>

        {/* Credentials */}
        <DeviceCredentialsPanel
          siteId={device.site_id}
          deviceId={deviceSlug}
          deviceLabel={device.label || `${device.spec_brand} ${device.spec_model}`}
        />

        {/* Photos placeholder */}
        <Section title="Photos">
          <div className="flex gap-2">
            <div style={{
              width: 60, height: 60, background: 'var(--primary-soft)',
              borderRadius: 'var(--radius)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, color: 'var(--primary)', cursor: 'pointer',
              border: '1px dashed rgba(37,99,235,0.4)',
            }}>+</div>
          </div>
        </Section>
      </div>

      {/* Footer actions */}
      <div className="flex gap-2" style={{
        padding: '12px 20px', borderTop: '1px solid var(--border-light)', flexShrink: 0,
      }}>
        <Button variant="secondary" style={{ flex: 1 }}>Edit</Button>
        <Button variant="secondary" style={{ flex: 1 }}>Trace Cable</Button>
        <Button style={{ flex: 1 }}>View on Plan</Button>
      </div>
    </div>
  )
}

interface WirelessClient {
  id: string
  hostname: string | null
  display_name: string | null
  mac: string
  ip: string | null
  rssi: number | null
  channel: number | null
  radio: string | null
  is_online: number
  tx_rate: number | null
  rx_rate: number | null
  tx_bytes: number | null
  rx_bytes: number | null
  satisfaction: number | null
}

function APWirelessClients({ siteId, apMac, apName }: { siteId: string; apMac: string | null; apName: string | null }) {
  const [clients, setClients] = useState<WirelessClient[]>([])
  const [loading, setLoading] = useState(true)
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    if (!apMac) { setLoading(false); return }
    api.get<{ data: WirelessClient[] }>(`/sites/${siteId}/clients?ap_mac=${encodeURIComponent(apMac)}&limit=200`)
      .then(res => setClients(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [siteId, apMac])

  const onlineCount = clients.filter(c => c.is_online).length
  const INITIAL_LIMIT = 15
  const visible = showAll ? clients : clients.slice(0, INITIAL_LIMIT)
  const hiddenCount = clients.length - INITIAL_LIMIT

  // Channel summary
  const channelMap = new Map<string, number>()
  clients.forEach(c => {
    if (c.channel) {
      const key = `Ch ${c.channel}${c.radio ? ` (${c.radio})` : ''}`
      channelMap.set(key, (channelMap.get(key) || 0) + 1)
    }
  })

  function fmtSpeed(rateBps: number | null) {
    if (!rateBps) return null
    const mbps = rateBps / 1000000
    if (mbps >= 1000) return `${(mbps / 1000).toFixed(1)} Gbps`
    if (mbps >= 1) return `${mbps.toFixed(0)} Mbps`
    return `${(rateBps / 1000).toFixed(0)} Kbps`
  }

  return (
    <Section title="Wireless Clients">
      {loading ? (
        <div style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>Loading...</div>
      ) : clients.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--text-tertiary)', padding: '8px 0' }}>
          No wireless clients connected{apName ? ` to ${apName}` : ''}
        </div>
      ) : (
        <>
          <div className="flex gap-2" style={{ marginBottom: 12 }}>
            <div style={{ flex: 1, padding: '8px 12px', background: 'var(--bg-inset)', borderRadius: 'var(--radius)' }}>
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>Connected</div>
              <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--ok)' }}>{onlineCount}</div>
            </div>
            <div style={{ flex: 1, padding: '8px 12px', background: 'var(--bg-inset)', borderRadius: 'var(--radius)' }}>
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>Total</div>
              <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{clients.length}</div>
            </div>
          </div>
          {channelMap.size > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
              {[...channelMap.entries()].map(([ch, count]) => (
                <span key={ch} style={{
                  padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                  background: 'var(--bg-inset)', color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)',
                }}>{ch}: {count}</span>
              ))}
            </div>
          )}
          <div style={{ background: 'var(--bg-inset)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
            {visible.map((cl, i) => (
              <div key={cl.id} style={{
                padding: '8px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                borderTop: i > 0 ? '1px solid var(--border-light)' : undefined,
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{cl.display_name || cl.hostname || cl.mac}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}>
                    {cl.ip || cl.mac}
                    {cl.channel != null && ` · Ch ${cl.channel}`}
                    {cl.radio && ` · ${cl.radio}`}
                    {fmtSpeed(cl.tx_rate) && ` · ${fmtSpeed(cl.tx_rate)}`}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {cl.rssi != null && (
                    <span style={{
                      fontSize: 11, fontFamily: 'var(--font-mono)', fontWeight: 600,
                      color: cl.rssi > -50 ? 'var(--ok)' : cl.rssi > -70 ? 'var(--warn)' : 'var(--err)',
                    }}>{cl.rssi} dBm</span>
                  )}
                  <div style={{
                    width: 6, height: 6, borderRadius: 3,
                    background: cl.is_online ? 'var(--ok)' : 'var(--color-infra)',
                  }} />
                </div>
              </div>
            ))}
            {!showAll && hiddenCount > 0 && (
              <button
                onClick={() => setShowAll(true)}
                style={{
                  width: '100%', padding: '10px 14px', border: 'none', background: 'transparent',
                  cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--primary)',
                  textAlign: 'center', fontFamily: 'inherit',
                  borderTop: '1px solid var(--border-light)',
                }}
              >
                Show all {clients.length} clients (+{hiddenCount} more)
              </button>
            )}
            {showAll && clients.length > INITIAL_LIMIT && (
              <button
                onClick={() => setShowAll(false)}
                style={{
                  width: '100%', padding: '10px 14px', border: 'none', background: 'transparent',
                  cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--text-tertiary)',
                  textAlign: 'center', fontFamily: 'inherit',
                  borderTop: '1px solid var(--border-light)',
                }}
              >
                Show fewer
              </button>
            )}
          </div>
        </>
      )}
    </Section>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      {title && (
        <div style={{
          fontSize: 12, fontWeight: 700, color: 'var(--text-tertiary)',
          textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8,
        }}>{title}</div>
      )}
      {children}
    </div>
  )
}
